import { inject, Injectable } from '@angular/core';
import { type Observable, of, throwError } from 'rxjs';

import { type RuntimeContext } from '@blaze-shared/elements-domain';
import { IntelliSenseService } from '@blaze-shared/intellisense-feature';
import { DatasetRowsService, SchemaService } from '@blaze-shared/schema-data-access';
import { type DatasetRow } from '@blaze-shared/schema-domain';
import { type MergeWorkflowNode } from '@blaze-shared/workflow-domain';

import { NodeResultMessagesService } from '../../node-result-messages.service';

/**
 * Writes a list of rows to a dataset in one pass, inserting, updating and deleting as the rows say.
 *
 * Version 2 nodes carry assignments, so each row is rebuilt from them — keeping its id, and its
 * deletion marker where it has one — while version 1 nodes send their input rows through as they
 * are.
 */
@Injectable({ providedIn: 'root' })
export class DatasetMergeActionService {
  private readonly datasetRowsService = inject(DatasetRowsService);
  private readonly iss = inject(IntelliSenseService);
  private readonly nodeResultMessages = inject(NodeResultMessagesService);
  private readonly schemaService = inject(SchemaService);

  datasetMerge(
    node: MergeWorkflowNode,
    nodeInput: DatasetRow[],
    ctx: RuntimeContext,
    workflowId?: string,
    workflowExecutionId?: string,
  ): Observable<any> {
    if (!node.datasetId) {
      return of(nodeInput);
    }
    const input = node.input ? ctx[node.input] : nodeInput;
    let data: DatasetRow[] = input ?? [];

    if (node.version >= 2) {
      if (!Array.isArray(input)) {
        return throwError(() => new Error('merge: input is not an array'));
      }
      const idColumn = this.schemaService.getIdColumnName(node.datasetId);
      data = input.map((row) => this.buildRow(node, row, ctx, idColumn));
    }

    const { errorMessage, successMessage } = this.nodeResultMessages.getErrorAndSuccessMessage(
      node,
      input,
      ctx,
    );
    return this.datasetRowsService.merge(
      node.datasetId,
      data,
      successMessage,
      errorMessage,
      workflowId,
      workflowExecutionId,
      true,
    );
  }

  private buildRow(
    node: MergeWorkflowNode,
    row: DatasetRow,
    ctx: RuntimeContext,
    idColumn: string,
  ): DatasetRow {
    const context = this.iss.updateRuntimeContext(ctx, row);
    const evaluatedAssignments = Object.fromEntries(
      node.assignments.map(({ name, value }) => {
        const columnName = this.schemaService.getColumnName(name);
        if (!columnName) {
          throw new Error('Column name missing');
        }
        return [columnName, this.iss.eval(value, context)];
      }),
    );

    return {
      [idColumn]: row[idColumn],
      ...evaluatedAssignments,
      ...(row._isDeleted !== undefined ? { _isDeleted: row._isDeleted } : {}),
    };
  }
}
